/**
 * sessionApi · 维度链架构 v6 · 会话/维度链/建夹任务 HTTP 接口封装
 *
 * 后端「彻底统一到 dimension_op」后，LLM 只产出维度操作，文件夹由服务端确定性
 * expand。前端通过本模块的接口：
 *   · 拿维度链预览（懒加载 + ETag 缓存）
 *   · 确认创建（前端按钮触发 → 入队 build_task）
 *   · 查单个建夹任务状态（轻量轮询）
 *
 * 全部 POST + JSON body，credentials:'include'（BFF 从 USER cookie 反查身份注入头）。
 * 路由前缀 /web/workgroup/folder/ai/chat（与 init/send 一致）。
 *
 * group_id 注入约定（与 ai_chat_proxy.go 对齐）
 * ──────────────────────────────────────────────
 * BFF AiChatProxy 共用入口要求 **每个** body 必带 group_id（UUID 形态，dao.Group
 * .GetGroupByRealID 反查短数字 group_id 注入上游 X-Group-ID header）。缺失 →
 * 400 {error:"group_id required"}。本模块把 group_id 作为所有接口的**必填首参**，
 * 由调用方从 redux state.group.get('groupId') 注入；postJSON 内部统一注入到 body
 * 减少调用方失误。
 */

import type {
  ClassifySessionDetail,
  SessionPreviewResponse,
  BuildTaskDetail,
  LayerValuesResponse
} from '../../../types/ai-classify'

declare const __API_SERVER_HOST__: string

const BASE = '/web/workgroup/folder/ai/chat'

/**
 * 统一 POST JSON 请求 · 自动注入 group_id（BFF AiChatProxy 必填）。
 *
 * 错误解析兼容三种返回形状：
 *   · BFF middleware.Response：`{code, msg, data:{error}}`（preview 400 实测）
 *   · 老格式裸 error：`{error: "..."}`
 *   · 兜底：`HTTP <status> <statusText>`
 *
 * 304（preview ETag 命中）→ 返回 null，调用方沿用上次缓存。
 */
async function postJSON<T>(
  path: string,
  groupID: string,
  body: Record<string, unknown>,
  extraHeaders?: Record<string, string>
): Promise<T> {
  if (!groupID) {
    // 主动失败优于丢到后端拿 400 — 让调用栈一眼能看见"groupID 没注入"
    throw new Error('group_id 未就绪，请先选择团队')
  }
  const res = await fetch(__API_SERVER_HOST__ + BASE + path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(extraHeaders || {}) },
    body: JSON.stringify({ ...body, group_id: groupID })
  })
  if (res.status === 304) {
    return null as unknown as T
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status} ${res.statusText}`
    // respBody 单独命名，避免与上面的请求 body 形参混淆
    let respBody: any = null
    try {
      respBody = await res.json()
      if (
        respBody &&
        respBody.data &&
        typeof respBody.data.error === 'string'
      ) {
        msg = respBody.data.error
      } else if (respBody && typeof respBody.error === 'string') {
        msg = respBody.error
      } else if (respBody && typeof respBody.msg === 'string') {
        msg = respBody.msg
      }
    } catch {
      /* ignore body parse error, keep statusText */
    }
    // 把 status + 解析 body 挂到 Error 上：接口3 422 配额拦截（reason/used/cap/
    // batch_n/over_by/member_kind/content）靠这里透出，其它调用方仍只读 message。
    const err = new Error(msg) as Error & { status?: number; body?: any }
    err.status = res.status
    err.body = respBody
    throw err
  }
  const json = await res.json()
  return json && json.data !== undefined ? json.data : json
}

// ---- session ----

/** 显式开新 session（场景 G：用户「我想换种分类」/ 检测到无 active session） */
export function postSessionNew(
  groupID: string,
  conversationId: string
): Promise<ClassifySessionDetail> {
  return postJSON('/session/new', groupID, { conversation_id: conversationId })
}

/** 查 session 详情 + 维度链摘要 */
export function getSession(
  groupID: string,
  sessionId: string
): Promise<ClassifySessionDetail> {
  return postJSON('/session/get', groupID, { session_id: sessionId })
}

/**
 * 实时 expand 维度链 → 懒加载子树。
 *
 * @param expandPath 按 folder name 从根逐层下钻；空 = 看顶层
 * @param etag       上次响应的 chain_version；命中（304）返回 null，调用方沿用缓存
 */
export function fetchPreview(
  groupID: string,
  sessionId: string,
  opts?: {
    expandPath?: string[]
    topOffset?: number
    topLimit?: number
    etag?: number
  }
): Promise<SessionPreviewResponse | null> {
  const headers: Record<string, string> = {}
  if (opts && typeof opts.etag === 'number') {
    headers['If-None-Match'] = String(opts.etag)
  }
  return postJSON<SessionPreviewResponse | null>(
    '/session/preview',
    groupID,
    {
      session_id: sessionId,
      expand_path: (opts && opts.expandPath) || [],
      top_offset: (opts && opts.topOffset) || 0,
      top_limit: (opts && opts.topLimit) || 100
    },
    headers
  )
}

/**
 * 前端「确认创建」按钮 → 入队 build_task（毫秒级返回）。
 * @param excludedKeys 用户反选未勾选的文件夹 key，空 = 全建
 */
export function postConfirm(
  groupID: string,
  sessionId: string,
  excludedKeys: string[] = []
): Promise<{
  task_id: string
  session_id: string
  total: number
  status: string
}> {
  return postJSON('/session/confirm', groupID, {
    session_id: sessionId,
    excluded_keys: excludedKeys
  })
}

// ---- build_task ----

/**
 * 查单个建夹任务详情 (含 status / done / top_folders 全嵌套树等)。
 *
 * 调用方: useBuildTaskDetail (BuildTaskStatusMessage 组件内挂的 hook) — 组件
 * 挂载首拉一次, 非终态 3s 一次轮询; 终态时 (done) 同时触发 FolderList 刷新。
 * 多任务历史会话各条 message 各自挂 hook 并行拉, 互不阻塞。
 *
 * 原配套的列表/ACK/folders 树/重试/取消接口随「文件夹创建任务列表」业务一起
 * 下线 (后端接口保留, 前端无调用方)。
 */
export function getBuildTask(
  groupID: string,
  taskId: string
): Promise<BuildTaskDetail> {
  return postJSON('/task', groupID, { task_id: taskId })
}

// ---- 自由选择 picker (2026-05-18 截图 9/10/11) ----

/**
 * 拉某 layer 的当前 values 列表 (前端 multiselect picker 渲染用).
 *
 * 后端从 ai_classify_dimension_layer.values_json 反序列化 → 扁平 list 返回;
 * keyword 服务端按 name substring 大小写不敏感过滤; 分页 offset/limit (默认 200, 上限 500).
 */
export function fetchLayerValues(
  groupID: string,
  sessionId: string,
  layerId: number,
  opts?: { keyword?: string; offset?: number; limit?: number }
): Promise<LayerValuesResponse> {
  return postJSON<LayerValuesResponse>('/layer/values', groupID, {
    session_id: sessionId,
    layer_id: layerId,
    keyword: (opts && opts.keyword) || '',
    offset: (opts && opts.offset) || 0,
    limit: (opts && opts.limit) || 200
  })
}

/**
 * 前端 UI 多选后回写: 服务端套 keep_keys filter, values_json 只保留 selected_keys,
 * chain_version+1 让 preview ETag 失效, 前端拉新预览即可看到收窄结果.
 */
export function postLayerValuesSelect(
  groupID: string,
  sessionId: string,
  layerId: number,
  selectedKeys: string[]
): Promise<{ ok: boolean; layer_id: number; kept_count: number }> {
  return postJSON('/layer/values/select', groupID, {
    session_id: sessionId,
    layer_id: layerId,
    selected_keys: selectedKeys
  })
}
